import {Ability, AbilityBuilder} from '@casl/ability'
import {useSelector} from 'react-redux'
import {store} from '../redux/store';

const ability = new Ability()

export default (action: any, subject: any) => {
    return ability.can(action, subject)
};

const buildRules = (permissions: any) => {
    // @ts-ignore
    const {can, rules} = new AbilityBuilder()

    // This logic depends on how the
    // server sends you the permissions array
    if (permissions) {
        permissions.forEach((p: any) => {
            can('able', p)
        })
    }

    return rules
}

// Keep the ability in sync with auth state. Previously it was built once at
// module load — before login/store-rehydration — so CAN() reflected empty
// permissions for the whole session (hiding controls the user is entitled to).
// Rebuild whenever the user's permission set changes; the reference compare
// keeps this cheap on unrelated dispatches.
let lastPermission: any
const applyPermissions = (permission: any) => {
    if (permission === lastPermission) return
    lastPermission = permission
    // @ts-ignore
    ability.update(buildRules(permission))
}
// `store` is `export let store` assigned inside configureStore(), so it is undefined
// until index.tsx calls it. Today this module only ever loads from a lazy route chunk,
// which is after that call — but ES imports are evaluated before the importing module's
// body, so anything that pulled can.ts into the main chunk would have crashed the app at
// boot on `store.getState()`. Optional-chain the store itself, not just its state.
const sync = () => applyPermissions(store?.getState?.()?.Auth?.user?.permission)
sync()
store?.subscribe?.(sync)

/**
 * Subscribe a component subtree to the permission set.
 *
 * `CAN()` reads a module-level mutable Ability that React knows nothing about. The
 * store subscription above keeps that object current, but updating it triggers no
 * re-render, so a permission arriving after a component's last render stayed invisible:
 * the gated button was simply absent, with no error anywhere, until something unrelated
 * happened to re-render the page (which is why a hard reload "fixed" it).
 *
 * Call this in an ancestor of anything gated by CAN(). Two things then hold:
 *
 *   1. `useSelector` re-renders that subtree whenever the permission array identity
 *      changes, so a refreshed grant reaches the UI immediately.
 *   2. The ability is rebuilt DURING this component's render, before React renders any
 *      child. Relying on the store subscription alone would be a race — react-redux
 *      registers its subscriber when the Provider mounts, ahead of the one in this
 *      module, so subscribers could re-render a frame before the rules were updated.
 */
export const useSyncedPermissions = (): string[] => {
    const permission = useSelector((s: any) => s?.Auth?.user?.permission);
    applyPermissions(permission);
    return Array.isArray(permission) ? permission : [];
};
